import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { resolveImageUrl } from "../lib/images";

export default function HeroCarousel() {
    const [slides, setSlides] = useState([]);
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        api.get("/slides")
            .then((r) => setSlides(r.data))
            .catch(() => setSlides([]));
    }, []);

    const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
    const scrollTo = useCallback((i) => emblaApi?.scrollTo(i), [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
        emblaApi.on("select", onSelect);
        onSelect();
        // autoplay
        const interval = setInterval(() => {
            if (emblaApi) emblaApi.scrollNext();
        }, 4000);
        return () => {
            emblaApi.off("select", onSelect);
            clearInterval(interval);
        };
    }, [emblaApi]);

    if (slides.length === 0) {
        return (
            <section className="relative pt-24 px-6 md:px-12 lg:px-20 h-[60vh] flex items-center justify-center bg-blush">
                <p className="font-serif text-3xl text-charcoal">Cargando...</p>
            </section>
        );
    }

    return (
        <section className="relative pt-20" data-testid="hero-carousel">
            <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex">
                    {slides.map((slide) => (
                        <div key={slide.id} className="flex-[0_0_100%] relative h-[78vh] min-h-[520px] max-h-[780px]">
                            {slide.image && (
                                <img
                                    src={resolveImageUrl(slide.image)}
                                    alt={slide.title}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-r from-charcoal/55 via-charcoal/25 to-transparent" />
                            <div className="relative z-10 h-full flex items-center px-6 md:px-12 lg:px-24">
                                <div className="max-w-2xl text-white animate-fade-up">
                                    <p className="overline !text-white/80">{slide.subtitle}</p>
                                    <h1 className="mt-3 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                                        {slide.title}
                                    </h1>
                                    {slide.button_text && (
                                        <Link
                                            to={slide.button_link || "/catalogo"}
                                            className="mt-8 inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-white rounded-full px-8 py-3.5 transition-colors text-xs tracking-[0.2em] uppercase"
                                            data-testid={`slide-cta-${slide.id}`}
                                        >
                                            {slide.button_text}
                                            <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {slides.length > 1 && (
                <>
                    <button
                        onClick={scrollPrev}
                        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/80 hover:bg-white text-charcoal items-center justify-center backdrop-blur transition shadow-lg"
                        data-testid="carousel-prev"
                        aria-label="Anterior"
                    >
                        <ChevronLeft strokeWidth={1.5} />
                    </button>
                    <button
                        onClick={scrollNext}
                        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/80 hover:bg-white text-charcoal items-center justify-center backdrop-blur transition shadow-lg"
                        data-testid="carousel-next"
                        aria-label="Siguiente"
                    >
                        <ChevronRight strokeWidth={1.5} />
                    </button>
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-testid="carousel-dots">
                        {slides.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => scrollTo(i)}
                                className={`h-1.5 rounded-full transition-all ${
                                    i === selectedIndex ? "w-8 bg-white" : "w-1.5 bg-white/50"
                                }`}
                                aria-label={`Ir a slide ${i + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}
